<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, inject } from "vue";
import { useRoute } from "vue-router";
import { DatasetDetailsDataset, useRuntimeEnv } from "@piveau/piveau-hub-ui-modules";
import DatasetRevisionContainer from "./DatasetRevisionContainer.vue";
import { useStore } from 'vuex';
import ECDistributions from "./ECDistributions.vue";

const store = useStore();
const route = useRoute();
const env = useRuntimeEnv();
const getID = computed(() => store.getters['datasetDetails/getID']);
const locale = route.query.locale;
const supportedLinkedDataFormats = ["rdf", "n3", "jsonld", "ttl", "nt"];

const triggerDatasetDownloadFromQuery = () => {
  const datasetId = route.params?.ds_id;
  const dlParam = route.query?.dl;
  const isDatasetBasePath = /^\/datasets\/[^/]+$/.test(route.path);
  const hasDlQueryParam = Object.prototype.hasOwnProperty.call(route.query, "dl");

  const normalizedDlParam = Array.isArray(dlParam) ? dlParam[0] : dlParam;
  const hasValidDlValue =
    typeof normalizedDlParam === "string" && normalizedDlParam.trim().length > 0;

  if (!datasetId || !isDatasetBasePath || !hasDlQueryParam || !hasValidDlValue) {
    return;
  }

  const format = normalizedDlParam.toLowerCase();
  if (!supportedLinkedDataFormats.includes(format)) {
    return;
  }

  const localeQuery = route.query?.locale
    ? `&locale=${encodeURIComponent(String(route.query.locale))}`
    : "";
  const downloadUrl = `${env.api.hubUrl}${route.path}.${format}?useNormalizedId=true${localeQuery}`;

  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = `${datasetId}.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface SelectedItem {
  label: string;
  value: string;
}

const selectedItem = ref<SelectedItem[]>([]);
const selectedVersion = ref("");
const title = ref("");

const revisionsList: any = ref([]);

const originalTitle = ref(document.title);

const datasetTitle = computed(() => {
  const titleObj = store.getters['datasetDetails/getTitle'];
  return titleObj?.[locale] || titleObj?.en || titleObj?.[Object.keys(titleObj)[0]] || 'Dataset';
});

watch(datasetTitle, (newTitle) => {
  if (newTitle) {
    document.title = newTitle;
  }
}, { immediate: true });


onUnmounted(() => {
  document.title = originalTitle.value;
});

onMounted(async () => {
  triggerDatasetDownloadFromQuery();

  const injected = inject("myObject");

  if (!injected) {
    throw new Error("abc");
  }
  watch(injected.apiData, (newValue) => {
    selectedItem.value = newValue;
    if (selectedItem.value.length > 0) {
      selectedVersion.value = selectedItem.value[0].value;
      title.value = injected.titleValue.value;
    }
  });
  watch(injected.revisionData, (newValue) => {
    revisionsList.value = newValue;
  });
});

watch(
  () => route.fullPath,
  () => {
    triggerDatasetDownloadFromQuery();
  }
);
</script>
<template>
  <DatasetDetailsDataset>

    <template #property-table-before>
      <div class="page-buttons d-none d-lg-block">
        <router-link :to="`/datasets/${getID}/quality`" class="page-btn">{{ $t('message.datasetDetails.subnav.quality') }}
          <img class="ml-3" src="../../assets/icon/arrow-right.svg"> </router-link>
          <router-link :to="`/datasets/${getID}/similarDatasets`"
          class="page-btn">{{ $t('message.datasetDetails.subnav.similarDatasets') }} <img class="ml-3"
            src="../../assets/icon/arrow-right.svg"></router-link>
          <router-link :to="`/datasets/${getID}/history`"
          class="page-btn">{{ $t('message.datasetDetails.subnav.history') }} <img class="ml-3"
            src="../../assets/icon/arrow-right.svg"></router-link>
      </div>
    </template>

    <template #distributions="slotProps">
      <ECDistributions v-bind="slotProps" />
    </template>

    <template v-if="selectedVersion" #property-table-after>
      <DatasetRevisionContainer :title="title" :selectedItem="selectedItem" :options="selectedItem"
        v-model="selectedVersion" :revisions="revisionsList" class="mb-3" />
    </template>
  </DatasetDetailsDataset>
</template>

<style scoped lang="scss">

:deep(.dsd-banners .ecl-message--info) {
  display: none !important;
}

.page-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 35px;
}

.page-btn {
  margin-bottom: 10px;
  height: 48px;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #fff;
  background: #0E47CB;
  justify-content: center;

  &:hover {
    background-color: #3E6CD5;
  }

}

</style>
